import React from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useNavigate } from "react-router-dom";
import { useGetActiveVenuesQuery, useGetVenueByIdQuery } from "../store/services/venues";
import { Venue } from "../models/venues";

interface VenueSelectorProps {
  tenantId?: string;
  selectedVenueId?: number | null;
  onVenueSelect: (venue: Venue | null) => void;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  onBlur?: () => void;
}

export const VenueSelector = ({
  tenantId,
  selectedVenueId: externalVenueId = null,
  onVenueSelect,
  disabled = false,
  required = false,
  invalid = false,
  onBlur,
}: VenueSelectorProps) => {
  const navigate = useNavigate();
  const selectedVenueId = externalVenueId;
  const previousTenantId = React.useRef<string | undefined>(tenantId);

  const { data: venues, isLoading } = useGetActiveVenuesQuery({
    tenantId: tenantId || undefined,
  });

  const { data: selectedVenue, isLoading: isLoadingSelectedVenue } = useGetVenueByIdQuery(
    selectedVenueId || "",
    { skip: !selectedVenueId }
  );

  // Reset selection when tenant changes
  React.useEffect(() => {
    if (previousTenantId.current === tenantId) {
      return;
    }

    const hadTenant = previousTenantId.current !== undefined;
    previousTenantId.current = tenantId;

    // Si recién se resolvió el tenant del form no hubo cambio del operador: no perder el venue.
    if (!hadTenant) {
      return;
    }

    onVenueSelect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const venueOptions = React.useMemo(() => {
    const options = Array.isArray(venues)
      ? venues.map((v) => ({
          label: `${v.name} — ${v.address}`,
          value: v.id,
          venue: v,
        }))
      : [];

    if (selectedVenue && !options.some((option) => option.value === selectedVenue.id)) {
      options.push({
        label: `${selectedVenue.name} — ${selectedVenue.address}`,
        value: selectedVenue.id,
        venue: selectedVenue,
      });
    }

    return options;
  }, [venues, selectedVenue]);

  return (
    <div className="col-12 my-2">
      <span className="p-float-label">
        <Dropdown
          className={`w-full ${invalid ? "p-invalid" : ""}`}
          id="venueId"
          value={selectedVenueId}
          options={venueOptions}
          onChange={(e) => {
            const selected = venueOptions.find((v) => v.value === e.value);
            onVenueSelect(selected?.venue || null);
          }}
          onBlur={onBlur}
          loading={isLoading || isLoadingSelectedVenue}
          disabled={disabled}
          placeholder="Seleccioná un venue"
          showClear={!required}
          filter
          filterPlaceholder="Buscar venue..."
          emptyMessage="No hay venues activos"
          emptyFilterMessage="Sin resultados"
        />
        <label htmlFor="venueId">{required ? "Venue *" : "Venue"}</label>
      </span>
      <div className="flex flex-wrap align-items-center gap-2 mt-2">
        <small className="text-color-secondary">
          Seleccioná un venue del listado para completar la dirección. Si falta uno, crealo antes de guardar el evento.
        </small>
        <Button
          type="button"
          label="Crear venue"
          icon="pi pi-plus"
          size="small"
          text
          onClick={() => navigate("/venues/new")}
        />
      </div>
    </div>
  );
};
